import { css } from '@emotion/react';

import { formatUsd } from '@/Client/Presenters';
import { styleguideColors as COLORS, styleguideFontWeight as WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

/**
 * The two things the navigator says over and over, in one place each: how a
 * selectable row looks, and how an amount reads.
 *
 * Both were previously spelled out at all three levels of the hierarchy — the
 * scope bar, a group header, a task row — which is exactly where a shared visual
 * language goes to drift. It already had: the accent bar was a box-shadow in two
 * places and a positioned pseudo-element in the third.
 */

/**
 * Every control in the navigator's tools row stands the same height. The gear
 * sets it: it is the one that must fit an icon and nothing else, so it has the
 * least room to give.
 */
export const TOOLS_CONTROL_HEIGHT = 24;

/**
 * Hover and selection for a row the user can pick — scope, group, or task.
 *
 * Hover and selection are two tints of the same lemon so a selected row under
 * the cursor still reads as selected, and selection adds a leading accent bar.
 * Compose it with the row's own layout:
 * `css={[ROW_STYLE, selectableRowStyle({ selected })]}`.
 *
 * Selection comes in as a prop rather than being read back off a data attribute
 * — React already knows, so there is no reason to round-trip it through the DOM.
 *
 * Apply only to the row itself, never to a container holding other rows — an
 * accent bar on a group wrapper runs the full height of its task list and reads
 * as "every task in here is selected".
 *
 * @param accentBarWidth width of the leading bar; tasks wear a heavier one than
 *   the group and scope rows above them.
 */
export const selectableRowStyle = (
  { selected, accentBarWidth = 3 }: { selected: boolean; accentBarWidth?: number },
) =>
  css`
    cursor: pointer;
    transition: background 120ms ease;

    &:hover { background: ${COLORS.wash}; }

    ${
    selected
    && css`
      background: ${COLORS.selectedWash};
      box-shadow: inset ${accentBarWidth}px 0 0 0 ${COLORS.accentDeep};

      /* Hover must not wash the selection out from under the cursor. */
      &:hover { background: ${COLORS.selectedWash}; }
    `
  }
  `;

/**
 * A row's trailing amount stacked above whatever control follows it — a task's
 * approve chip, a group's bulk approve chip. Shared so a task row and a group
 * header read as the same idea at two scales rather than two hand-aligned
 * layouts that happen to look similar.
 */
export const TRAILING_STACK_STYLE = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
`;

/**
 * An amount. Body face with tabular figures so amounts line up down the sidebar,
 * and always ink — colour belongs to the status pill, not the number.
 *
 * Size and weight climb with what the number covers: a task, then its group,
 * then the whole scope.
 */
export const amountStyle = (fontSize: number, fontWeight: number = WEIGHT.medium) =>
  css`
    font-family: ${FONT.body};
    font-size: ${pxToRem(fontSize)};
    font-weight: ${fontWeight};
    letter-spacing: -0.01em;
    color: ${COLORS.ink};
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  `;

/**
 * The placeholder for a missing or zero task cost: `formatUsd`'s "$--", the
 * reading a task nothing has been costed on gets. Named so a caller can compare
 * against it instead of the bare literal (see `KaiNavTaskRow`'s `costIsZero`,
 * and `KaiNavigator.tsx`'s `DEFAULT_GROUP_COST` for the equivalent one level up).
 */
export const DEFAULT_TASK_COST_LABEL = formatUsd(undefined);

/**
 * Muted text for a row whose own amount reads as a placeholder: a task at
 * DEFAULT_TASK_COST_LABEL, or a group at DEFAULT_GROUP_COST. Shared so
 * "nothing costed here yet" reads the same way at every level of the hierarchy.
 */
export const MUTED_TEXT_STYLE = css`
  color: ${COLORS.textTertiary};
`;

/**
 * A rollup amount, or nothing at all when there is nothing to roll up.
 *
 * Most groups in a scope are untouched, so printing `$0` down the sidebar fills
 * it with numbers that say only "not this one". Absence carries that better, and
 * it leaves the amounts that do exist easy to pick out.
 */
export const formatNavigatorRollup = (value: number | undefined): string | undefined => value ? formatUsd(value) : undefined;
